var _excluded = ["isOpen", "onOpen", "onTourStart", "onTourExit"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useContext } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';
import { pxToRem } from "../../../common";
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import BrandButton from "../../BrandButton";
import { TourContext } from "../context";

/**
 * @typedef TourStep
 * @object
 * @property {string} target HTML Selector for placement of Tour popup.
 * @property {string} content Content to be placed inside of Tour popup.
 */

/**
 * @function TourStepEventCallback
 * @param {TourStep} tourStep This TourStep object
 * @param {any?} params Optional params
 */

/**
 * @typedef TourFeature
 * @property {number} id ID of the Tour Feature
 * @property {string} title Title of Tour Feature
 * @property {string} description Description of Tour Feature
 * @property {Step[]} steps Array of `Step`s for the Feature tour
 * @property {boolean} completed Whether this tour has been completed or not.
 * @property {TourStepEventCallback} onStepStart Callback function to be invoked when Feature tour has begun.
 * @property {TourStepEventCallback} onStepEnd Callback function to be invoked when Feature tour has concluded.
 */

var StyledAccordion = styled(Accordion)(function (_ref) {
  var theme = _ref.theme;
  return {
    borderRadius: '3px',
    border: 'none',
    paddingInline: '8px',
    paddingBlock: '8px',
    background: theme.palette.tour.feature.background,
    color: theme.palette.tour.feature.text,
    // MUI Accordion adds a pseudo border before
    // the component. This removes it.
    '&:before': {
      display: 'none'
    }
  };
});

/**
 * Creates a UI element containing information about a specified feature. Feature can contain an array
 * of steps which the user can walk through.
 * @param {TourFeature} params - Feature object
 * @return {JSX.Element}
 * @constructor
 */
export function TourFeature(_ref2) {
  var isOpen = _ref2.isOpen,
    onOpen = _ref2.onOpen,
    onTourStart = _ref2.onTourStart,
    onTourExit = _ref2.onTourExit,
    step = _objectWithoutProperties(_ref2, _excluded);
  var id = step.id,
    title = step.title,
    description = step.description,
    steps = step.steps,
    onStepStart = step.onStepStart,
    onStepEnd = step.onStepEnd,
    completed = step.completed;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var theme = useTheme();
  var _useContext = useContext(TourContext),
    currentActiveTourStep = _useContext.currentActiveTourStep,
    startTour = _useContext.startTour,
    exitTour = _useContext.exitTour,
    clearCurrentTour = _useContext.clearCurrentTour;
  var isActionHidden = !(steps !== null && steps !== void 0 && steps.length);
  function handleTourStart() {
    if (currentActiveTourStep) {
      clearCurrentTour();
    }
    startTour(step);
    if (onStepStart) {
      onStepStart(step);
    }
    if (onTourStart) {
      onTourStart(step);
    }
  }
  function handleEndTour() {
    exitTour();
    if (onStepEnd) {
      onStepEnd(step);
    }
    if (onTourExit) {
      onTourExit(step);
    }
  }
  return /*#__PURE__*/React.createElement(StyledAccordion, {
    disableGutters: true,
    expanded: isOpen,
    onChange: function onChange() {
      return onOpen({
        id: id
      });
    }
  }, /*#__PURE__*/React.createElement(AccordionSummary, {
    expandIcon: /*#__PURE__*/React.createElement(ExpandMore, {
      sx: {
        color: 'tour.feature.text'
      }
    }),
    sx: {
      minHeight: '36px',
      margin: 0,
      padding: 0,
      '.MuiAccordionSummary-content': {
        margin: 0
      },
      display: 'flex',
      alignItems: 'center',
      gap: pxToRem(8)
    }
  }, /*#__PURE__*/React.createElement(Checkbox, {
    disabled: true,
    checked: completed,
    icon: /*#__PURE__*/React.createElement(CircleTwoToneIcon, {
      sx: {
        color: theme.palette.tour.feature.checkbox.background.incomplete
      }
    }),
    checkedIcon: /*#__PURE__*/React.createElement(CheckCircleRoundedIcon, {
      color: "success"
    }),
    sx: {
      paddingLeft: 0
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    component: "div",
    sx: {
      fontSize: pxToRem(13),
      fontWeight: 700,
      marginBlock: 'auto',
      width: '180px'
    }
  }, t(title))), /*#__PURE__*/React.createElement(AccordionDetails, {
    sx: {
      p: 0
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    component: "p",
    sx: {
      fontSize: pxToRem(13),
      width: '245px'
    }
  }, t(description, '')), !isActionHidden && /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: '8px'
    }
  }, !((currentActiveTourStep === null || currentActiveTourStep === void 0 ? void 0 : currentActiveTourStep.id) === id) ? /*#__PURE__*/React.createElement(BrandButton, {
    onClick: function onClick() {
      return handleTourStart();
    },
    sx: {
      width: 'min-content',
      whiteSpace: 'nowrap'
    }
  }, t('tour_feature_action_start_tour')) : /*#__PURE__*/React.createElement(BrandButton, {
    onClick: function onClick() {
      return handleEndTour();
    },
    sx: {
      width: 'min-content',
      whiteSpace: 'nowrap'
    }
  }, t('tour_feature_action_end_tour')))));
}